// src/pages/B2B/crm/api/types.ts

export const PIPELINE_STAGES = [
  '1-Target',
  '2-Researching',
  '3-Contacted',
  '4-In Conversation',
  '5-Free Tier Active',
  '6-Paid',
] as const;
export type PipelineStage = typeof PIPELINE_STAGES[number];

// Curated suggestion lists. Each rendered via SelectWithOther — values are
// stored as free-text in TEXT columns, so users can pick a suggestion or type
// a custom value. Keep these in lockstep with shared/crm.py constants.
export const OUTREACH_STATUSES = [
  'Not Started',
  'Escalated To Ray',
  'Ray In Conversation',
  'Proposal Sent',
  'Closed',
] as const;

export const VENUE_TYPES = [
  'Bar',
  'Club',
  'Restaurant Bar',
  'Restaurant',
  'Rooftop',
  'Sports Bar',
] as const;

export const OWNERSHIP_TYPES = [
  'Independent',
  'Corporate Group',
  'Franchise',
] as const;

export const SUBSCRIPTION_TIERS = [
  'None',
  'Starter',
  'Pro',
  'Partner',
  'Enterprise',
] as const;

export const CONTACT_KINDS = ['decision_maker', 'on_site'] as const;
export type ContactKind = typeof CONTACT_KINDS[number];

// The list endpoint now returns the full venue record so the pipeline table
// can render every column. Keep VenueListRow as an alias for clarity.
export type VenueListRow = Venue;

export interface Venue {
  id: string;
  name: string;
  address: string | null;
  venue_type: string | null;
  ownership_type: string | null;
  parent_company: string | null;
  total_locations: number | null;

  pipeline_stage: PipelineStage | null;
  flag_for_ray: boolean;
  // Free-text suggestion field (see OUTREACH_STATUSES). Backend stores as TEXT;
  // "Other" entries from SelectWithOther flow through unchanged.
  corporate_outreach_status: string | null;

  last_7d_checkins: number | null;
  last_30d_checkins: number | null;
  peak_night: string | null;
  social_media_mention: boolean;
  docsend_opened: boolean;
  docsend_open_date: string | null;

  first_contact_date: string | null;
  last_contact_date: string | null;
  last_contact_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  pushes_sent: number;

  subscription_tier: string | null;
  monthly_revenue: string | null;  // numeric → string
  on_crawl_route: boolean;
  warm_intro_source: string | null;
  notes: string | null;

  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface VenueContact {
  id: string;
  venue_id: string;
  name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  kind: ContactKind;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface VenueListResponse {
  count: number;
  venues: VenueListRow[];
}

export interface VenueDetailResponse {
  venue: Venue;
  contacts: VenueContact[];
}

export interface VenueListFilters {
  stage?: PipelineStage;
  flag_for_ray?: boolean;
  search?: string;
  sort?: 'last_contact_date' | 'name' | 'next_action_date';
}

// ── Meetings ──────────────────────────────────────────────────────────────────

export interface Meeting {
  id: string;
  venue_id: string;
  title: string;
  starts_at: string;       // ISO timestamp (UTC)
  ends_at: string;         // ISO timestamp (UTC)
  all_day: boolean;
  attendees: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export interface MeetingListResponse {
  count: number;
  meetings: Meeting[];
}

export interface MeetingListFilters {
  from?: string;        // ISO timestamp (UTC)
  to?: string;          // ISO timestamp (UTC)
  venue_id?: string;
}

export interface MeetingCreatePayload {
  venue_id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  attendees?: string | null;
  notes?: string | null;
}

export type MeetingUpdatePayload = Partial<Omit<MeetingCreatePayload, 'venue_id'>>;
